import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiDownload, FiExternalLink, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { END_POINT, getJwtToken } from '../../../Redux/AdminReducer/action';
import { getAllDropDowns } from '../../../Redux/Store/API/DropDownApi';
import RightWrapper from '../../../Components/CommonComponents/ComponentWrapper';
import { educationLevels } from '../../../Files/dropdownOptions';
import diagnosisGroups from '../../../Files/diagnosis.json';
import { useLinkedDropdown } from '../../../Services/linkedDropdowns';
import exportToExcel from '../../../UtilityHookFunctions/exportToExcel';

const authHeaders = () => ({ headers: { Authorization: getJwtToken() } });

const PLACEHOLDER = 'Choose the Right Option';
const PAGE_SIZES = [10, 25, 50, 100];

const THERAPY_OPTIONS = [
  { value: 'any', label: 'Any therapy' },
  { value: 'none', label: 'No therapy' },
  ...['nutrition', 'counselling', 'acupuncture', 'acupressure', 'ayurvedic', 'neuropathy', 'physiotherapy']
    .map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) })),
];
const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// Filters backed by the clinic's DropDown lists: the query param, its label and
// the DropDown keys to read options from (first non-empty list wins).
const DROPDOWN_FILTERS = [
  { name: 'statusByVisit', label: 'Status as per Visit', keys: ['status_by_appointment'] },
  { name: 'statusByTreatment', label: 'Status as per Treatment', keys: ['status_by_treatment'] },
  { name: 'patientType', label: 'Type of Patient', keys: ['type_of_patient'] },
  { name: 'gender', label: 'Gender', keys: ['gender'] },
  { name: 'reference', label: 'Reference (how they heard of us)', keys: ['reference_for_enquiry'] },
  { name: 'patientReference', label: 'Referred by (relation)', keys: ['patient_reference', 'family'] },
  { name: 'religion', label: 'Religion', keys: ['religion'] },
  { name: 'maritalStatus', label: 'Marital Status', keys: ['marital_status'] },
  { name: 'motherTongue', label: 'Mother Tongue', keys: ['mother_tongue'] },
  { name: 'bloodGroup', label: 'Blood Group', keys: ['blood_group'] },
];

// The configured options of the first non-empty list, trimmed and de-duplicated case-insensitively.
const listFrom = (dropDownData, keys) => {
  for (const key of keys) {
    const seen = new Set();
    const list = (Array.isArray(dropDownData?.[key]) ? dropDownData[key] : [])
      .map((v) => String(v ?? '').trim())
      .filter((v) => {
        const k = v.toLowerCase();
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    if (list.length) return list;
  }
  return [];
};

const EMPTY_FILTERS = {
  q: '',
  diagnosis: '',
  location: '',
  city: '',
  area: '',
  package: '',
  courier: '',
  courierCity: '',
  courierName: '',
  alternativeTherapy: '',
  education: '',
  industry: '',
  profession: '',
  ...Object.fromEntries(DROPDOWN_FILTERS.map((f) => [f.name, ''])),
  feesMin: '',
  feesMax: '',
  referralBusiness: '',
  referralBusinessId: '',
  durationMin: '',
  durationMax: '',
  beforeAfter: '',
  caseStudy: '',
  review: '',
  startDate: '',
  endDate: '',
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-GB');
};
const fmtMoney = (v) => (v ? `₹${Number(v).toLocaleString('en-IN')}` : '—');
const diagnosisText = (d) => [d?.primary, d?.secondary, d?.tertiary].filter(Boolean).join(', ') || '—';
const toOptions = (list) => (list || []).map((v) => (typeof v === 'object' ? v : { value: v, label: v }));

const flattenDiagnosis = () => {
  const out = [];
  (Array.isArray(diagnosisGroups) ? diagnosisGroups : []).forEach((group) => {
    Object.values(group || {}).forEach((items) => {
      (Array.isArray(items) ? items : []).forEach((item) => out.push(item));
    });
  });
  return [...new Set(out)].sort((a, b) => a.localeCompare(b));
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const inputClass =
  'w-full border border-slate-200 rounded-md p-2 text-sm bg-white focus:ring-blue-500 focus:border-blue-500';

const SelectField = ({ label, name, value, onChange, options }) => (
  <Field label={label}>
    <select name={name} value={value} onChange={onChange} className={inputClass}>
      <option value="">{PLACEHOLDER}</option>
      {toOptions(options).map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </Field>
);

const TextField = ({ label, name, value, onChange, type = 'text', placeholder, list }) => (
  <Field label={label}>
    <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} list={list} className={inputClass} />
  </Field>
);

const FlagBadge = ({ on, children }) =>
  on ? (
    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
      {children}
    </span>
  ) : null;

const EXPORT_COLUMNS = [
  { key: 'patientId', label: 'Patient ID' },
  { key: 'name', label: 'Name' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'email', label: 'Email' },
  { key: 'gender', label: 'Gender' },
  { key: 'city', label: 'City' },
  { key: 'area', label: 'Area' },
  { key: 'state', label: 'State' },
  { key: 'diagnosis.primary', label: 'Primary Diagnosis' },
  { key: 'diagnosis.secondary', label: 'Secondary Diagnosis' },
  { key: 'diagnosis.tertiary', label: 'Tertiary Diagnosis' },
  { key: 'package', label: 'Package' },
  { key: 'education', label: 'Education' },
  { key: 'industry', label: 'Industry' },
  { key: 'profession', label: 'Profession' },
  ...DROPDOWN_FILTERS.map((f) => ({ key: f.name, label: f.label })),
  { key: 'registeredOn', label: 'Registered On' },
  { key: 'visits', label: 'Visits' },
  { key: 'totalPaid', label: 'Total Fees Paid' },
  { key: 'firstVisit', label: 'First Visit', format: (v) => (v ? fmtDate(v) : '') },
  { key: 'lastVisit', label: 'Last Visit', format: (v) => (v ? fmtDate(v) : '') },
  { key: 'treatmentDays', label: 'Treatment Duration (days)', format: (v) => (v ?? '') },
  { key: 'courierCount', label: 'Courier Records' },
  { key: 'referralBusinesses', label: 'Referral Business', format: (v) => (v || []).join(', ') },
  { key: 'therapies', label: 'Alternative Therapy', format: (v) => (v || []).join(', ') },
  { key: 'beforeAfter', label: 'Before / After', format: (v) => (v ? 'Yes' : 'No') },
  { key: 'caseStudy', label: 'Case Study', format: (v) => (v ? 'Yes' : 'No') },
  { key: 'review', label: 'Review', format: (v) => (v ? 'Yes' : 'No') },
];

const buildParams = (filters, extra = {}) => {
  const params = new URLSearchParams();
  Object.entries({ ...filters, ...extra }).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) params.append(k, v);
  });
  return params.toString();
};

export default function PatientSearch() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dropDownData = useSelector((state) => state?.dropDown?.getallDropDowns?.[0] || {});
  const { parents: industryParents, childrenOf: professionsOf } = useLinkedDropdown('industry');

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [options, setOptions] = useState({});
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    dispatch(getAllDropDowns());
    axios
      .get(`${END_POINT}/patient-search/options`, authHeaders())
      .then(({ data }) => setOptions(data?.data || {}))
      .catch(() => setOptions({}));
  }, [dispatch]);

  const diagnosisOptions = useMemo(flattenDiagnosis, []);
  // Configured list only; the built-in levels are a fallback when the clinic has none (never merged)
  const configuredEducation = listFrom(dropDownData, ['education']);
  const educationOptions = configuredEducation.length > 0 ? configuredEducation : educationLevels;
  // Packages come from the DropDown list; stored values only when the clinic configured none
  const configuredPackages = listFrom(dropDownData, ['package']);
  const packageOptions = configuredPackages.length > 0 ? configuredPackages : options.packages;
  const moreActive = DROPDOWN_FILTERS.filter((f) => filters[f.name] !== '').length;
  const industryOptions = industryParents.length > 0 ? industryParents : dropDownData?.industry || [];
  const professionOptions = useMemo(() => {
    const linked = filters.industry ? professionsOf(filters.industry) : [];
    if (linked.length) return linked;
    return dropDownData?.profession || [];
  }, [filters.industry, professionsOf, dropDownData?.profession]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${END_POINT}/patient-search?${buildParams(applied, { page, limit })}`,
        authHeaders()
      );
      setRows(data?.data || []);
      setTotal(data?.pagination?.total || 0);
    } catch {
      toast.error('Could not load patients');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      // A profession from another industry no longer applies
      ...(name === 'industry' ? { profession: '' } : {}),
    }));
  };

  const applyFilters = (e) => {
    e?.preventDefault();
    setPage(1);
    setApplied({ ...filters });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(
        `${END_POINT}/patient-search?${buildParams(applied, { all: true })}`,
        authHeaders()
      );
      const list = data?.data || [];
      if (!list.length) {
        toast.info('No patients to export');
        return;
      }
      exportToExcel(list, `patient_search_${new Date().toISOString().slice(0, 10)}`, EXPORT_COLUMNS, 'Patients');
    } catch {
      toast.error('Could not export patients');
    } finally {
      setExporting(false);
    }
  };

  const pages = Math.max(Math.ceil(total / limit), 1);
  const activeCount = Object.values(applied).filter((v) => v !== '').length;

  return (
    <RightWrapper>
      <div className="p-6 w-full">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Patient Search</h1>
            <p className="text-sm text-slate-500">
              Find patients by diagnosis, location, package, courier, therapy, profile, fees, treatment duration and case flags.
            </p>
          </div>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !total}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
          >
            <FiDownload className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
        </div>

        <form onSubmit={applyFilters} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <TextField label="Search (name / phone / patient ID)" name="q" value={filters.q} onChange={handleChange} placeholder="Type to search…" />
            <SelectField label="Diagnosis" name="diagnosis" value={filters.diagnosis} onChange={handleChange} options={diagnosisOptions} />
            <TextField label="Location (any address field)" name="location" value={filters.location} onChange={handleChange} placeholder="City, area, state, pincode…" />
            <SelectField label="City" name="city" value={filters.city} onChange={handleChange} options={options.cities} />
            <SelectField label="Area" name="area" value={filters.area} onChange={handleChange} options={options.areas} />
            <SelectField label="Package" name="package" value={filters.package} onChange={handleChange} options={packageOptions} />
            <SelectField label="Courier" name="courier" value={filters.courier} onChange={handleChange} options={YES_NO} />
            <SelectField label="Courier City" name="courierCity" value={filters.courierCity} onChange={handleChange} options={options.courierCities} />
            <SelectField label="Courier Partner" name="courierName" value={filters.courierName} onChange={handleChange} options={options.courierNames} />
            <SelectField label="Alternative Therapy" name="alternativeTherapy" value={filters.alternativeTherapy} onChange={handleChange} options={THERAPY_OPTIONS} />
            <SelectField label="Education" name="education" value={filters.education} onChange={handleChange} options={educationOptions} />
            <SelectField label="Industry" name="industry" value={filters.industry} onChange={handleChange} options={industryOptions} />
            <SelectField label="Profession" name="profession" value={filters.profession} onChange={handleChange} options={professionOptions} />
            <TextField label="Fees Paid — Min (₹)" name="feesMin" type="number" value={filters.feesMin} onChange={handleChange} />
            <TextField label="Fees Paid — Max (₹)" name="feesMax" type="number" value={filters.feesMax} onChange={handleChange} />
            <SelectField label="Referral Business" name="referralBusiness" value={filters.referralBusiness} onChange={handleChange} options={YES_NO} />
            <SelectField
              label="Referral Business Name"
              name="referralBusinessId"
              value={filters.referralBusinessId}
              onChange={handleChange}
              options={(options.referralBusinesses || []).map((b) => ({ value: b._id, label: b.name }))}
            />
            <TextField label="Treatment Duration — Min (days)" name="durationMin" type="number" value={filters.durationMin} onChange={handleChange} />
            <TextField label="Treatment Duration — Max (days)" name="durationMax" type="number" value={filters.durationMax} onChange={handleChange} />
            <SelectField label="Before / After" name="beforeAfter" value={filters.beforeAfter} onChange={handleChange} options={YES_NO} />
            <SelectField label="Case Study" name="caseStudy" value={filters.caseStudy} onChange={handleChange} options={YES_NO} />
            <SelectField label="Review" name="review" value={filters.review} onChange={handleChange} options={YES_NO} />
            <TextField label="Registered From" name="startDate" type="date" value={filters.startDate} onChange={handleChange} />
            <TextField label="Registered To" name="endDate" type="date" value={filters.endDate} onChange={handleChange} />
          </div>
          <div className="mt-4 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => setShowMore((v) => !v)}
              aria-expanded={showMore}
              className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline"
            >
              {showMore ? <FiChevronUp className="h-4 w-4" /> : <FiChevronDown className="h-4 w-4" />}
              More filters{moreActive > 0 ? ` (${moreActive})` : ''}
            </button>
            {showMore && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
                {DROPDOWN_FILTERS.map((f) => (
                  <SelectField
                    key={f.name}
                    label={f.label}
                    name={f.name}
                    value={filters[f.name]}
                    onChange={handleChange}
                    options={listFrom(dropDownData, f.keys)}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <FiRefreshCw className="h-4 w-4" /> Reset
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <FiSearch className="h-4 w-4" /> Search
            </button>
          </div>
        </form>

        <div className="flex flex-wrap justify-between items-center gap-3 mb-3 text-sm text-slate-600">
          <span>
            {loading ? 'Loading…' : `${total} patient${total === 1 ? '' : 's'} found`}
            {activeCount > 0 && ` · ${activeCount} filter${activeCount === 1 ? '' : 's'} applied`}
          </span>
          <label className="flex items-center gap-2">
            Rows per page
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              className="border border-slate-200 rounded-md p-1 text-sm"
            >
              {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>

        <div className="overflow-x-auto bg-white rounded-xl border border-slate-200 shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Patient</th>
                <th className="px-4 py-3">Patient ID</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">City / Area</th>
                <th className="px-4 py-3">Diagnosis</th>
                <th className="px-4 py-3">Package</th>
                <th className="px-4 py-3">Profession</th>
                <th className="px-4 py-3">Fees Paid</th>
                <th className="px-4 py-3">First Visit</th>
                <th className="px-4 py-3">Last Visit</th>
                <th className="px-4 py-3">Duration</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={13} className="px-4 py-10 text-center text-slate-500">No patients match these filters.</td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r._id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{r.name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.patientId || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.mobile || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{[r.city, r.area].filter(Boolean).join(' / ') || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{diagnosisText(r.diagnosis)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.package !== '' && r.package !== null ? r.package : '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.profession || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtMoney(r.totalPaid)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(r.firstVisit)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(r.lastVisit)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.treatmentDays !== null && r.treatmentDays !== undefined ? `${r.treatmentDays} days` : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      <FlagBadge on={r.beforeAfter}>Before/After</FlagBadge>
                      <FlagBadge on={r.caseStudy}>Case Study</FlagBadge>
                      <FlagBadge on={r.review}>Review</FlagBadge>
                      <FlagBadge on={r.courierCount > 0}>Courier</FlagBadge>
                      <FlagBadge on={r.referralCount > 0}>Referral</FlagBadge>
                      <FlagBadge on={(r.therapies || []).length > 0}>{(r.therapies || []).join(', ')}</FlagBadge>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/patients/${r._id}/profile`)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"
                    >
                      <FiExternalLink className="h-3.5 w-3.5" /> Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap justify-between items-center gap-3 mt-4 text-sm">
          <span className="text-slate-600">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => Math.min(p + 1, pages))}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </RightWrapper>
  );
}
